import React, { useEffect, useState } from 'react';
import Container from '@material-ui/core/Container';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import Sidebar, { SIDEBAR_COLLAPSED_WIDTH } from "./Sidebar";
import Header from "./Header";
import variables from "../../Sys/variable.scss";
import ChatbotWidget from '../ChatbotWidget';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sidebarCollapsed';

const useStyles = makeStyles((theme) => ({
  lockedShell: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 1,
  },
  content: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    padding: (props) => (props.lockPageScroll ? 0 : theme.spacing(2)),
    backgroundColor: variables.bodyColor,
    minHeight: (props) => (props.lockPageScroll ? 0 : 'calc(100vh - 95px)'),
    height: (props) => (props.lockPageScroll ? '100%' : 'auto'),
    overflow: (props) => (props.lockPageScroll ? 'hidden' : 'visible'),
    boxSizing: 'border-box',
    flex: (props) => (props.lockPageScroll ? 1 : undefined),
    display: (props) => (props.lockPageScroll ? 'flex' : undefined),
    flexDirection: (props) => (props.lockPageScroll ? 'column' : undefined),
    '@media (max-width: 374px)': {
      padding: (props) => (props.lockPageScroll ? 0 : theme.spacing(1)),
    },
    '@media (min-width: 1280px)': {
      width: (props) => (props.lockPageScroll ? 'auto' : 'calc(100% - 220px)'),
      flex: (props) => (props.lockPageScroll ? 1 : undefined),
      padding: (props) => (props.lockPageScroll ? 0 : theme.spacing(2)),
    },
  },
  mainSec: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: (props) => (props.lockPageScroll ? 0 : 'calc(100vh - 95px)'),
    flex: (props) => (props.lockPageScroll ? 1 : undefined),
    minWidth: 0,
    maxWidth: '100%',
    overflowX: 'hidden',
    overflowY: (props) => (props.lockPageScroll ? 'hidden' : 'visible'),
    '@media (min-width: 1280px)': {
      flexDirection: 'row',
      alignItems: (props) => (props.lockPageScroll ? 'stretch' : undefined),
    },
  },
  childContent: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3),
      paddingLeft: theme.spacing(4),
    },
  }
}));

const Layout = ({ sidebarView = true, showHeader = true, lockPageScroll = false, children }) => {
  const classes = useStyles({ lockPageScroll });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDrawerMode = useMediaQuery(theme.breakpoints.down(1280));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
    } catch (error) {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(sidebarCollapsed));
    } catch (error) {
      // Ignore storage errors (e.g. private browsing mode).
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!lockPageScroll) {
      return undefined;
    }

    const html = document.documentElement;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      html.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [lockPageScroll]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleToggleSidebarCollapse = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const layoutBody = (
    <>
      {showHeader && <Header onMenuClick={handleDrawerToggle} showHamburger={isDrawerMode} />}
      <div className={classes.mainSec} style={!showHeader && !lockPageScroll ? { minHeight: '100vh' } : undefined}>
        {sidebarView ? <>
          <Sidebar
            mobileOpen={mobileOpen}
            onMobileClose={handleDrawerToggle}
            isMobile={isMobile}
            collapsed={sidebarCollapsed}
            onToggleCollapse={handleToggleSidebarCollapse}
            constrainHeight={lockPageScroll}
          />
          <Container maxWidth={false} className={classes.content} style={lockPageScroll ? { maxWidth: '100%', minWidth: 0 } : { width: '100%', maxWidth: '100%' }} disableGutters={lockPageScroll} >
            {children}
          </Container>
        </> :
          <Container style={{ width: '100%', maxWidth: '100%', padding: 0 }} >
            {children}
          </Container>}
      </div>
    </>
  );

  return (
    <>
      {lockPageScroll ? <div className={classes.lockedShell}>{layoutBody}</div> : layoutBody}
      {!lockPageScroll && <ChatbotWidget collapsedSidebarWidth={sidebarView && !isDrawerMode && sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : 0} />}
    </>
  );
}

export default Layout;